import PublishedModelTableTooltip from "./PublishedModelTableTooltip";
import React from "react";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneIcon from "@mui/icons-material/Tune";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import LaborDriverSalesBuilderModal from "./LaborDriverSalesBuilderModal";
import LaborDriverRoutedSalesModal from "./LaborDriverRoutedSalesModal";


function LaborDriversSalesTable(props) {
  const [salesLaborDrivers, setSalesLaborDrivers] = React.useState([]);
  const [builderOpen, setBuilderOpen] = React.useState(false);
  const [editingDriver, setEditingDriver] = React.useState(null);
  const [routedSalesOpen, setRoutedSalesOpen] = React.useState(false);

  const [backfillModalOpen, setBackfillModalOpen] = React.useState(false);
  const [selectedBackfillDriverUuids, setSelectedBackfillDriverUuids] =
    React.useState([]);
  const [backfillResults, setBackfillResults] = React.useState(null);
  const [backfillLoading, setBackfillLoading] = React.useState(false);
  const [backfillError, setBackfillError] = React.useState(null);

  React.useEffect(() => {
    const filteredDrivers = (props.laborDrivers || []).filter(
      (driver) => driver.labor_driver_sales !== null
    );
    setSalesLaborDrivers(filteredDrivers);
  }, [props.laborDrivers]);

  const allDriverUuids = React.useMemo(
    () => salesLaborDrivers.map((driver) => driver.uuid),
    [salesLaborDrivers]
  );

  const hasSalesLaborDrivers = salesLaborDrivers.length > 0;
  const selectedDriverCount = selectedBackfillDriverUuids.length;

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    maxHeight: "80vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: 24,
    padding: "24px",
    overflowY: "auto",
  };

  const aggregateLabelLookup = {
    sum: "Sum",
    count: "Count",
    distinct_count: "Distinct count",
  };

  const formatFieldLabel = (value) =>
    String(value || "")
      .replace(/_/g, " ")
      .replace(/\bid\b/gi, "ID")
      .replace(/\b\w/g, (character) => character.toUpperCase());

  const formatNumber = (value) => {
    if (value === null || value === undefined) {
      return "—";
    }
    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
  };

  const isNoFilterDriver = (driver) => {
    const filterColumn = driver?.labor_driver_sales?.filter_column || "";
    const filterValues = driver?.labor_driver_sales?.filter_values || [];
    return (
      !filterColumn ||
      (filterColumn.toLowerCase() === "key" &&
        filterValues.some((value) =>
          ["nofilter", "no filter", "all sales"].includes(
            String(value).toLowerCase()
          )
        ))
    );
  };

  const getFilterPresentation = (driver) => {
    if (isNoFilterDriver(driver)) {
      return {
        field: "All sales",
        values: "",
        valueItems: [],
        extraCount: 0,
      };
    }
    const filterColumn = driver?.labor_driver_sales?.filter_column || "";
    const filterValues = driver?.labor_driver_sales?.filter_values || [];
    return {
      field: formatFieldLabel(filterColumn),
      values: filterValues.slice(0, 2).join(", ") || "No values selected",
      valueItems: filterValues.slice(0, 2),
      extraCount: Math.max(filterValues.length - 2, 0),
    };
  };

  const getAggregatePresentation = (driver) => {
    const targetColumn = driver?.labor_driver_sales?.target_column || "";
    const aggregateType = driver?.labor_driver_sales?.aggregate_type || "";
    return {
      method: aggregateLabelLookup[aggregateType] || formatFieldLabel(aggregateType),
      field: formatFieldLabel(targetColumn),
    };
  };

  const handleOpenCreate = () => {
    setEditingDriver(null);
    setBuilderOpen(true);
  };

  const handleOpenEdit = (driver) => {
    setEditingDriver(driver);
    setBuilderOpen(true);
  };

  const handleCloseBuilder = () => {
    setBuilderOpen(false);
    setEditingDriver(null);
  };

  const handleOpenBackfillModal = () => {
    if (!hasSalesLaborDrivers) {
      return;
    }
    setSelectedBackfillDriverUuids(allDriverUuids);
    setBackfillResults(null);
    setBackfillError(null);
    setBackfillModalOpen(true);
  };

  const handleCloseBackfillModal = () => {
    if (backfillLoading) {
      return;
    }
    setBackfillModalOpen(false);
    setSelectedBackfillDriverUuids([]);
  };

  const toggleDriverSelection = (uuid) => {
    setBackfillError(null);
    setSelectedBackfillDriverUuids((prev) =>
      prev.includes(uuid)
        ? prev.filter((id) => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleSelectAllDrivers = () => {
    setBackfillError(null);
    setSelectedBackfillDriverUuids(allDriverUuids);
  };

  const handleClearAllDrivers = () => {
    setBackfillError(null);
    setSelectedBackfillDriverUuids([]);
  };

  const handleRunBackfill = async () => {
    if (!selectedBackfillDriverUuids.length) {
      setBackfillError("Select at least one sales group to continue.");
      return;
    }
    if (!props.backfill_labor_driver_sales) {
      setBackfillError("Backfill action is unavailable.");
      return;
    }

    setBackfillLoading(true);
    setBackfillError(null);

    try {
      const payload = await props.backfill_labor_driver_sales(
        selectedBackfillDriverUuids.length === allDriverUuids.length
          ? []
          : selectedBackfillDriverUuids
      );
      setBackfillResults(payload);
      toast.success("Actual sales backfill completed!", {
        autoClose: 3000,
      });
    } catch (error) {
      const message =
        error?.message ||
        error?.response?.data?.system?.message ||
        "Backfill request failed.";
      setBackfillError(message);
      toast.error(message, { autoClose: 4000 });
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <>
      <div className="px-6 pb-6 pt-7 sm:px-4">
        <div className="flex items-start justify-between gap-6 md:flex-col md:items-stretch">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-xl font-semibold text-slate-900">
                Sales drivers
              </h3>
              <span className="text-sm text-slate-500">
                {salesLaborDrivers.length} configured
              </span>
            </div>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Define the POS sales metrics that drive labor demand.
            </p>
          </div>

          {!props.readOnly ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                aria-label="Advanced routing"
                className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                onClick={() => setRoutedSalesOpen(true)}
                type="button"
              >
                <TuneIcon className="mr-1.5" sx={{ fontSize: 18 }} />
                Routing
              </button>
              {!props.scenarioMode ? (
                <button
                  className={`inline-flex h-10 items-center rounded-lg px-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 ${
                    hasSalesLaborDrivers
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "cursor-not-allowed bg-slate-200 text-slate-400"
                  }`}
                  onClick={handleOpenBackfillModal}
                  disabled={!hasSalesLaborDrivers}
                  type="button"
                >
                  Backfill sales
                </button>
              ) : null}
              <button
                className="inline-flex h-10 items-center rounded-lg bg-violet-700 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                onClick={handleOpenCreate}
                type="button"
              >
                <AddIcon className="mr-1.5" sx={{ fontSize: 19 }} />
                Add driver
              </button>
            </div>
          ) : null}
        </div>

        <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <PublishedModelTableTooltip readOnly={props.readOnly}>
            <table className="min-w-[760px] w-full table-fixed">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                      <th
                        scope="col"
                        className="w-[24%] px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="w-[50%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Sales Group
                      </th>
                      <th
                        scope="col"
                        className="w-[22%] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                      >
                        Calculation
                      </th>
                      <th scope="col" className="w-[4%] px-3 py-3">
                        <span className="sr-only">Edit</span>
                      </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {salesLaborDrivers.map((salesLaborDriver) => {
                  const filter = getFilterPresentation(salesLaborDriver);
                  const aggregate = getAggregatePresentation(salesLaborDriver);

                  return (
                    <tr
                      key={salesLaborDriver.uuid}
                      className={`group transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-violet-600 ${
                        props.readOnly
                          ? ""
                          : "cursor-pointer hover:bg-violet-50/50"
                      }`}
                      role={props.readOnly ? undefined : "button"}
                      tabIndex={props.readOnly ? undefined : 0}
                      aria-label={
                        props.readOnly
                          ? undefined
                          : `Edit ${salesLaborDriver.name}`
                      }
                      onClick={
                        props.readOnly
                          ? undefined
                          : () => handleOpenEdit(salesLaborDriver)
                      }
                      onKeyDown={
                        props.readOnly
                          ? undefined
                          : (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleOpenEdit(salesLaborDriver);
                              }
                            }
                      }
                    >
                      <td className="px-5 py-4 align-middle">
                        <p className="truncate text-sm font-semibold text-slate-900" title={salesLaborDriver.name}>
                          {salesLaborDriver.name}
                        </p>
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="inline-flex h-6 shrink-0 items-center rounded-md bg-slate-100 px-2 text-xs font-semibold text-slate-600">
                            {filter.field}
                          </span>
                          {filter.valueItems.length ? (
                            <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                              {filter.valueItems.map((value, valueIndex) => (
                                <span
                                  key={`${value}-${valueIndex}`}
                                  className="inline-flex h-6 min-w-0 items-center truncate rounded-md bg-violet-100 px-2 text-xs font-medium text-violet-700"
                                  title={value}
                                >
                                  {value}
                                </span>
                              ))}
                            </div>
                          ) : filter.values ? (
                            <span
                              className="min-w-0 truncate text-sm text-slate-600"
                              title={filter.values}
                            >
                              {filter.values}
                            </span>
                          ) : null}
                          {filter.extraCount > 0 ? (
                            <span className="inline-flex h-6 shrink-0 items-center rounded-md bg-violet-50 px-2 text-xs font-semibold text-violet-700">
                              +{filter.extraCount}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle">
                        <div className="flex min-w-0 items-center gap-2 text-sm">
                          <span className="shrink-0 font-semibold text-slate-700">
                            {aggregate.method}
                          </span>
                          <span className="text-slate-400">of</span>
                          <span className="truncate text-slate-600" title={aggregate.field}>
                            {aggregate.field}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-right align-middle">
                        {!props.readOnly ? (
                          <ChevronRightIcon
                            className="text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-700"
                            sx={{ fontSize: 21 }}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {!salesLaborDrivers.length ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-12 text-center text-sm text-slate-500"
                    >
                      No sales drivers yet. Add one to connect a POS metric to
                      the labor model.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            </PublishedModelTableTooltip>
          </div>
        </div>
      </div>

      <LaborDriverSalesBuilderModal
        open={builderOpen}
        driver={editingDriver}
        companyId={props.companyId}
        onClose={handleCloseBuilder}
        onSave={props.post_labor_driver_sales}
        onDelete={props.delete_labor_driver}
      />

      <LaborDriverRoutedSalesModal
        open={routedSalesOpen}
        onClose={() => setRoutedSalesOpen(false)}
        laborDrivers={props.laborDrivers}
        storesInfo={props.storesInfo}
        onSave={props.post_labor_driver_routed_sales}
        onBackfill={
          props.scenarioMode ? null : props.backfill_labor_driver_routed_sales
        }
        onDelete={props.delete_labor_driver}
        selectedCompany={props.selectedCompany}
      />

      <Modal open={backfillModalOpen} onClose={handleCloseBackfillModal}>
        <Box sx={modalStyle}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-slate-900">Backfill Actual Sales</h2>
            <button
              className="text-slate-400 hover:text-slate-600"
              onClick={handleCloseBackfillModal}
              type="button"
            >
              <CloseIcon />
            </button>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            Choose which sales groups to backfill. The task will calculate
            actual sales across all historical data we have for those groups.
          </p>
          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3">
              <button
                className="text-violet-700 hover:underline disabled:text-slate-400 disabled:no-underline"
                onClick={handleSelectAllDrivers}
                type="button"
                disabled={
                  backfillLoading || selectedDriverCount === allDriverUuids.length
                }
              >
                Select all
              </button>
              <span className="text-slate-300">|</span>
              <button
                className="text-violet-700 hover:underline disabled:text-slate-400 disabled:no-underline"
                onClick={handleClearAllDrivers}
                type="button"
                disabled={backfillLoading || selectedDriverCount === 0}
              >
                Clear
              </button>
            </div>
            <div className="text-xs text-slate-500">
              {selectedDriverCount} selected
            </div>
          </div>
          <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-slate-200">
            {salesLaborDrivers.map((driver) => {
              const isChecked = selectedBackfillDriverUuids.includes(driver.uuid);
              const filter = getFilterPresentation(driver);
              return (
                <div
                  key={driver.uuid}
                  className={`flex items-center gap-3 px-4 py-2 ${
                    backfillLoading ? "cursor-not-allowed" : "hover:bg-slate-50"
                  }`}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleDriverSelection(driver.uuid)}
                    disabled={backfillLoading}
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-700">
                      {driver.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {filter.field}
                      {filter.values ? `: ${filter.values}` : ""}
                      {filter.extraCount > 0
                        ? ` +${filter.extraCount} more`
                        : ""}
                    </div>
                  </div>
                </div>
              );
            })}
            {!salesLaborDrivers.length && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No sales groups are available yet.
              </div>
            )}
          </div>
          {backfillError && (
            <div className="mt-3 text-sm text-rose-600">{backfillError}</div>
          )}
          <div className="mt-6">
            <LoadingButton
              variant="contained"
              onClick={handleRunBackfill}
              loading={backfillLoading}
              disabled={
                backfillLoading ||
                !selectedBackfillDriverUuids.length ||
                !hasSalesLaborDrivers
              }
              sx={{
                width: "100%",
                textTransform: "none",
                borderRadius: "12px",
                paddingY: "12px",
                backgroundColor: "#6d28d9",
                "&:hover": {
                  backgroundColor: "#5b21b6",
                },
              }}
            >
              Run Backfill
            </LoadingButton>
          </div>
          {backfillResults && !backfillLoading && (
            <div className="mt-6">
              <div className="text-sm text-slate-500">
                Processed {backfillResults.days_processed} days (
                {backfillResults.start_date} to {backfillResults.end_date}) across{" "}
                {backfillResults.stores_considered} stores.
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="py-2 pr-3 text-left font-medium">
                        Sales group
                      </th>
                      <th className="py-2 pr-3 text-left font-medium">
                        Total value
                      </th>
                      <th className="py-2 pr-3 text-left font-medium">
                        Avg/store/day
                      </th>
                      <th className="py-2 pr-3 text-left font-medium">
                        Stores with data
                      </th>
                      <th className="py-2 text-left font-medium">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {backfillResults.metrics?.map((metric) => (
                      <tr key={metric.labor_driver_uuid}>
                        <td className="py-3 pr-3 font-medium text-slate-900">
                          {metric.labor_driver_name}
                        </td>
                        <td className="py-3 pr-3 text-slate-700">
                          {formatNumber(metric.total_value)}
                        </td>
                        <td className="py-3 pr-3 text-slate-700">
                          {formatNumber(metric.average_per_store_per_day)}
                        </td>
                        <td className="py-3 pr-3 text-slate-700">
                          {metric.stores_with_data} / {metric.stores_considered}
                        </td>
                        <td className="py-3 text-slate-700">
                          {metric.days_processed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Box>
      </Modal>
    </>
  );
}

export default LaborDriversSalesTable;
